import { z } from "zod";

import { adminTabSlugSchema } from "@/lib/admin-tabs";
import { CATALOG_PANES } from "@/lib/catalog-admin";

// The browser seam for the Admin route's search params — the same shape, and
// the same home, as `loginSearchSchema` beside it. The tab *vocabulary* itself
// is not here: it is named by both this file and the shell, so it lives in
// `@/lib/admin-tabs`, exactly as the `?error=` vocabulary lives in
// `@/lib/auth-error-codes`.
//
// Zod v4 implements Standard Schema, so this passes straight into the admin
// route's `validateSearch`. `.catch(undefined)` drops a malformed or retired
// slug instead of failing the navigation.
//
// A tab that wants a param of its own (the old stack's tabs carried `?surface=`,
// `?mode=`, `?email=`) adds it here, and the shell's tab links carry it across a
// tab switch — they merge onto the current params rather than replacing them.
export const adminSearchSchema = z.object({
  tab: adminTabSlugSchema.optional().catch(undefined),
  // The Item Catalog tab's own param — the first tab to want one. Its sub-tabs
  // are the URL for the same reasons the top bar's are: they deep-link, they
  // cmd-click, and the tab keeps no navigation state of its own. Named
  // `catalog` rather than a generic `pane` so a second tab growing sub-tabs
  // does not have to share this one's vocabulary.
  //
  // Dropped rather than rejected on a value this does not know, exactly like
  // `tab` above; the panel falls back to `DEFAULT_CATALOG_PANE`.
  catalog: z.enum(CATALOG_PANES).optional().catch(undefined),
  // The Integrations tab's direction (#747), the source's `?mode=`. **Any**
  // string is kept, unlike the two above: the source read the param and never
  // wrote it back (`ExternalApis.tsx:561-563`), so a value it did not know
  // opened the default and stayed in the address bar — and this schema is what
  // the router rewrites the URL to. What the tab *shows* for a value is
  // `effectiveIntegrationMode`'s call. The codec reads `mode` as literal text
  // (`@/lib/router-search-codec`), so a link never hands this a non-string;
  // the `catch` only guards an in-app navigation that did. A repeated `?mode=`
  // arrives as its list of values and is kept the same way.
  mode: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .catch(undefined),
});

export type AdminSearch = z.infer<typeof adminSearchSchema>;
